import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Building2, Loader2, Mail, ShieldCheck, Upload, Filter, Trash2, PhoneCall, Palette, Sparkles, UserCircle, KeyRound, Copy, RefreshCw, Clock, BookOpen, QrCode, Download, Printer, Share2 } from "lucide-react";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import QRCode from "qrcode";

function JoinCodeCard({ user }) {
  const [code, setCode] = useState("");
  const [joinRole, setJoinRole] = useState("agent");
  const [requireApproval, setRequireApproval] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [uses, setUses] = useState(0);
  const [busy, setBusy] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [savingLimits, setSavingLimits] = useState(false);
  const [pending, setPending] = useState([]);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [orgName, setOrgName] = useState("");
  const [orgLogo, setOrgLogo] = useState("");
  const [brandColor, setBrandColor] = useState("#0D9488");
  const [orgAddress, setOrgAddress] = useState("");
  const isAdmin = user.role === "admin";

  const loadOrg = () => api.get("/org").then(({ data }) => {
    setCode(data.join_code || "");
    setJoinRole(data.join_role || "agent");
    setRequireApproval(!!data.require_approval);
    setExpiresAt((data.join_code_expires_at || "").slice(0, 10));
    setMaxUses(data.join_code_max_uses ? String(data.join_code_max_uses) : "");
    setUses(data.join_code_uses || 0);
    setOrgName(data.name || "");
    setOrgLogo(data.logo || "");
    setBrandColor(data.brand_color || "#0D9488");
    setOrgAddress(data.address || "");
  }).catch(() => {});
  const loadPending = () => { if (user.role === "admin" || user.role === "manager") api.get("/team/pending").then(({ data }) => setPending(data)).catch(() => {}); };
  useEffect(() => { loadOrg(); loadPending(); }, []); // eslint-disable-line

  const copy = () => { navigator.clipboard?.writeText(code); toast.success("Join code copied"); };
  const joinUrl = `${window.location.origin}/register?code=${code}`;
  const shareLink = async () => {
    if (!code) return;
    if (navigator.share) {
      try { await navigator.share({ title: `Join ${orgName || "our agency"} on IShareLeads`, text: `Use join code ${code} to join our workspace.`, url: joinUrl }); return; }
      catch { /* user cancelled or unsupported — fall back to copy */ }
    }
    try { await navigator.clipboard.writeText(joinUrl); toast.success("Join link copied — paste it anywhere"); }
    catch { toast.error("Could not copy link"); }
  };
  const generateQr = async (size = 512) => {
    const canvas = document.createElement("canvas");
    await QRCode.toCanvas(canvas, joinUrl, { width: size, margin: 2, errorCorrectionLevel: "H", color: { dark: "#0f172a", light: "#ffffff" } });
    if (orgLogo) {
      try {
        const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = fileUrl(orgLogo); });
        const ctx = canvas.getContext("2d");
        const box = Math.round(size * 0.22);
        const pad = Math.round(size * 0.02);
        const bx = (size - box) / 2 - pad, by = (size - box) / 2 - pad, bs = box + pad * 2;
        const r = Math.round(bs * 0.16);
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.moveTo(bx + r, by);
        ctx.arcTo(bx + bs, by, bx + bs, by + bs, r);
        ctx.arcTo(bx + bs, by + bs, bx, by + bs, r);
        ctx.arcTo(bx, by + bs, bx, by, r);
        ctx.arcTo(bx, by, bx + bs, by, r);
        ctx.closePath();
        ctx.fill();
        const scale = Math.min(box / img.width, box / img.height);
        const w = img.width * scale, h = img.height * scale;
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
      } catch { /* logo failed — plain QR */ }
    }
    return canvas.toDataURL("image/png");
  };
  const openQr = async () => {
    try {
      const url = await generateQr(512);
      setQrUrl(url); setQrOpen(true);
    } catch { toast.error("Could not generate QR code"); }
  };
  const downloadQr = () => {
    if (!qrUrl) return;
    const a = document.createElement("a");
    a.href = qrUrl; a.download = `join-code-${code}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const printFlyer = async () => {
    if (!code) return;
    try {
      const qr = await generateQr(640);
      const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
      const color = /^#[0-9a-fA-F]{6}$/.test(brandColor) ? brandColor : "#0D9488";
      const roleLabel = joinRole === "manager" ? "Manager" : "Agent";
      const article = /^[aeiou]/i.test(roleLabel) ? "an" : "a";
      const logoImg = orgLogo ? `<img class="logo" src="${esc(fileUrl(orgLogo))}" crossorigin="anonymous" alt="" />` : "";
      const host = window.location.host;
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(orgName || "IShareLeads")} — Join code</title>
<style>
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; }
  body { margin:0; font-family:-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif; color:#0f172a; }
  .sheet { width:210mm; min-height:297mm; margin:0 auto; display:flex; flex-direction:column; }
  .top { background:${color}; color:#fff; padding:44px 56px 40px; text-align:center; }
  .top .logo { max-height:76px; max-width:280px; object-fit:contain; margin-bottom:18px; }
  .top h2 { margin:0; font-size:26px; font-weight:800; letter-spacing:.4px; }
  .top .sub { margin-top:6px; opacity:.9; font-size:14px; letter-spacing:1px; }
  .body { flex:1; padding:52px 56px 24px; text-align:center; display:flex; flex-direction:column; align-items:center; }
  .headline { font-size:38px; font-weight:900; line-height:1.12; max-width:560px; }
  .headline span { color:${color}; }
  .qrbox { margin:34px 0 18px; padding:18px; border:3px solid ${color}; border-radius:24px; }
  .qrbox img { width:300px; height:300px; display:block; }
  .label { text-transform:uppercase; letter-spacing:3px; font-size:12px; color:#64748b; }
  .code { font-family:ui-monospace,Menlo,Consolas,monospace; font-size:50px; font-weight:800; letter-spacing:10px; margin:4px 0; color:${color}; }
  .steps { margin-top:30px; display:flex; gap:22px; justify-content:center; max-width:650px; }
  .step { flex:1; }
  .step .n { width:34px; height:34px; border-radius:50%; background:${color}; color:#fff; font-weight:700; display:flex; align-items:center; justify-content:center; margin:0 auto 8px; font-size:15px; }
  .step p { margin:0; font-size:13px; color:#334155; line-height:1.4; }
  .foot { padding:16px 56px 40px; text-align:center; color:#94a3b8; font-size:12px; }
</style></head>
<body><div class="sheet">
  <div class="top">${logoImg}<h2>${esc(orgName || "Our Agency")}</h2><div class="sub">POWERED BY ISHARELEADS</div></div>
  <div class="body">
    <div class="headline">Join our team as ${article} <span>${esc(roleLabel)}</span></div>
    <div class="qrbox"><img src="${qr}" alt="Join QR" /></div>
    <div class="label">Company join code</div>
    <div class="code">${esc(code)}</div>
    <div class="steps">
      <div class="step"><div class="n">1</div><p>Scan the QR code with your phone camera, or visit <b>${esc(host)}/register</b></p></div>
      <div class="step"><div class="n">2</div><p>Choose <b>Join with a code</b> and enter <b>${esc(code)}</b></p></div>
      <div class="step"><div class="n">3</div><p>Create your account and start closing leads</p></div>
    </div>
  </div>
  <div class="foot">${esc(orgAddress ? orgAddress + " · " : "")}Recruit anywhere — hang this flyer at your onboarding events.</div>
</div>
<script>
  function go(){ try { window.focus(); } catch(e){} window.print(); }
  var imgs = Array.prototype.slice.call(document.images);
  var left = imgs.length;
  if(!left){ go(); }
  else { imgs.forEach(function(im){ if(im.complete){ if(--left===0) go(); } else { im.onload=im.onerror=function(){ if(--left===0) go(); }; } }); }
</script>
</body></html>`;
      const w = window.open("", "_blank");
      if (!w) { toast.error("Allow pop-ups to print the flyer"); return; }
      w.document.write(html); w.document.close();
    } catch { toast.error("Could not build the flyer"); }
  };
  const regen = async () => {
    if (!window.confirm("Generate a new code? The old code will stop working and the usage count resets.")) return;
    setBusy(true);
    try { const { data } = await api.post("/org/join-code/regenerate"); setCode(data.join_code); setUses(0); toast.success("New join code generated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const changeRole = async (role) => {
    setJoinRole(role); setSavingRole(true);
    try { await api.post("/org/join-role", { role }); toast.success(`Code sign-ups will join as ${role === "manager" ? "Manager" : "Agent"}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingRole(false); }
  };
  const toggleApproval = async (v) => {
    setRequireApproval(v);
    try { await api.post("/org/approval", { require_approval: v }); toast.success(v ? "New sign-ups now need your approval" : "Approval requirement turned off"); }
    catch (err) { setRequireApproval(!v); toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveLimits = async () => {
    setSavingLimits(true);
    try {
      await api.post("/org/join-code/limits", { expires_at: expiresAt || null, max_uses: maxUses ? Number(maxUses) : null });
      toast.success("Join code limits saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingLimits(false); }
  };
  const approve = async (m) => {
    try { await api.post(`/team/pending/${m.user_id}/approve`); toast.success(`${m.name} approved`); loadPending(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const reject = async (m) => {
    if (!window.confirm(`Reject ${m.name}? Their account will be removed.`)) return;
    try { await api.post(`/team/pending/${m.user_id}/reject`); toast.success(`${m.name} rejected`); loadPending(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Card className="p-6" data-testid="join-code-card">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15"><KeyRound className="h-5 w-5 text-primary" /></div>
        <div>
          <h3 className="font-heading font-semibold">Company join code</h3>
          <p className="text-xs text-muted-foreground">Share this code so another company or individual can join your workspace when they sign up.</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <code className="rounded-lg border border-red-700 bg-red-600 px-4 py-2 font-mono text-lg tracking-widest text-white" data-testid="join-code-value">{code || "—"}</code>
        <Button variant="outline" size="sm" onClick={copy} data-testid="copy-join-code"><Copy className="mr-1 h-4 w-4" /> Copy</Button>
        {isAdmin && <Button variant="outline" size="sm" onClick={regen} disabled={busy} data-testid="regen-join-code"><RefreshCw className="mr-1 h-4 w-4" /> Regenerate</Button>}
        {code && <Button variant="outline" size="sm" onClick={openQr} data-testid="qr-join-code"><QrCode className="mr-1 h-4 w-4" /> QR code</Button>}
        {code && <Button variant="outline" size="sm" onClick={shareLink} data-testid="share-join-link"><Share2 className="mr-1 h-4 w-4" /> Share link</Button>}
        <span className="text-xs text-muted-foreground">{uses} sign-up{uses !== 1 ? "s" : ""}{maxUses ? ` / ${maxUses} max` : ""}</span>
      </div>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="qr-dialog">
          <DialogHeader><DialogTitle>Company join QR code</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            {qrUrl && <img src={qrUrl} alt="Join code QR" className="h-56 w-56 rounded-lg border border-border bg-white p-2" data-testid="qr-image" />}
            <p className="text-center text-xs text-muted-foreground">Scan to open sign-up pre-filled with code <span className="font-mono font-semibold text-foreground">{code}</span>.</p>
            <div className="grid w-full grid-cols-2 gap-2">
              <Button variant="outline" onClick={downloadQr} data-testid="download-qr"><Download className="mr-1 h-4 w-4" /> Download PNG</Button>
              <Button onClick={printFlyer} data-testid="print-flyer"><Printer className="mr-1 h-4 w-4" /> Print flyer</Button>
            </div>
            <Button variant="ghost" size="sm" onClick={shareLink} className="w-full" data-testid="share-join-link-dialog"><Share2 className="mr-1 h-4 w-4" /> Copy sign-up link</Button>
          </div>
        </DialogContent>
      </Dialog>

      {isAdmin && (
        <div className="mt-4 grid gap-4 border-t border-border pt-4 sm:grid-cols-2">
          <div>
            <Label>New members join as</Label>
            <p className="mt-1 text-xs text-muted-foreground">Role assigned to anyone who uses this code.</p>
            <Select value={joinRole} onValueChange={changeRole} disabled={savingRole}>
              <SelectTrigger className="mt-2" data-testid="join-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="agent" data-testid="join-role-agent">Agent</SelectItem>
                <SelectItem value="manager" data-testid="join-role-manager">Manager</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Approval</Label>
            <p className="mt-1 text-xs text-muted-foreground">Hold code sign-ups until you approve them.</p>
            <label className="mt-2 flex items-center gap-2 text-sm"><Checkbox checked={requireApproval} onCheckedChange={(v) => toggleApproval(!!v)} data-testid="require-approval-toggle" /> Require admin approval</label>
          </div>
          <div className="min-w-0">
            <Label htmlFor="code-expiry">Expiry date (optional)</Label>
            <Input id="code-expiry" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="mt-2 w-full min-w-0" data-testid="join-code-expiry-input" />
          </div>
          <div className="min-w-0">
            <Label htmlFor="code-maxuses">Max uses (optional)</Label>
            <Input id="code-maxuses" type="number" min="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="Unlimited" className="mt-2 w-full min-w-0" data-testid="join-code-maxuses-input" />
          </div>
          <div className="sm:col-span-2">
            <Button size="sm" onClick={saveLimits} disabled={savingLimits} data-testid="save-join-limits">{savingLimits && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save limits</Button>
          </div>
        </div>
      )}

      {isAdmin && pending.length > 0 && (
        <div className="mt-4 border-t border-border pt-4" data-testid="pending-approvals">
          <h4 className="flex items-center gap-2 text-sm font-semibold"><Clock className="h-4 w-4 text-amber-500" /> Awaiting approval ({pending.length})</h4>
          <div className="mt-3 space-y-2">
            {pending.map((m) => (
              <div key={m.user_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2" data-testid={`pending-row-${m.user_id}`}>
                <div className="min-w-0 text-sm">
                  <span className="font-medium">{m.name}</span> <span className="text-muted-foreground">· {m.email}</span> <span className="capitalize text-muted-foreground">· {m.role}</span>{m.join_source && <span className="text-muted-foreground"> · {{ qr: "via QR", link: "via link", manual: "via code" }[m.join_source] || "via code"}</span>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => approve(m)} data-testid={`approve-${m.user_id}`}>Approve</Button>
                  <Button size="sm" variant="outline" className="text-destructive" onClick={() => reject(m)} data-testid={`reject-${m.user_id}`}>Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { InsuranceTypePicker } from "@/components/InsuranceTypePicker";
import { PushToggleCard } from "@/components/PushToggleCard";
import { fileUrl, initials } from "@/lib/fileUrl";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SOFTPHONE_SCHEMES } from "@/lib/dialer";

export default function OrgSettings() {
  const { user, checkAuth } = useAuth();
  const isAdmin = user.role === "admin";
  const [org, setOrg] = useState(null);
  const [name, setName] = useState("");
  const [orgTypes, setOrgTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState(user.lead_preferences || []);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [brandColor, setBrandColor] = useState("#0D9488");
  const [orgAddress, setOrgAddress] = useState("");
  const [partnerApproval, setPartnerApproval] = useState(false);
  const [autoBook, setAutoBook] = useState(false);
  const [phone, setPhone] = useState(user.phone || "");
  const [savingPhone, setSavingPhone] = useState(false);
  const fmtPhone = (v) => {
    const d = (v || "").replace(/\D/g, "").slice(0, 10);
    if (d.length < 4) return d;
    if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };
  const savePhone = async () => {
    setSavingPhone(true);
    try { await api.patch("/profile/phone", { phone }); await checkAuth(); toast.success("Phone number saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingPhone(false); }
  };

  const sp0 = user.softphone || {};
  const [spEnabled, setSpEnabled] = useState(!!sp0.enabled);
  const [spScheme, setSpScheme] = useState(sp0.scheme || "tel");
  const [spTemplate, setSpTemplate] = useState(sp0.template || "");
  const [savingSp, setSavingSp] = useState(false);
  const saveSoftphone = async () => {
    setSavingSp(true);
    try { await api.patch("/profile/softphone", { enabled: spEnabled, scheme: spScheme, template: spTemplate }); await checkAuth(); toast.success("Softphone settings saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingSp(false); }
  };

  const [isCloser, setIsCloser] = useState(!!user.is_closer);
  const [savingCloser, setSavingCloser] = useState(false);
  const [bio, setBio] = useState(user.bio || "");
  const [bioActive, setBioActive] = useState(!!user.bio_active);
  const [savingBio, setSavingBio] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const toggleCloser = async (v) => {
    setIsCloser(v); setSavingCloser(true);
    try { await api.patch("/profile/closer", { is_closer: v }); await checkAuth(); toast.success(v ? "You're now a Closer" : "Closer turned off"); }
    catch (err) { setIsCloser(!v); toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingCloser(false); }
  };
  const genBio = async () => {
    setGenBusy(true);
    try { const { data } = await api.post("/profile/bio/generate", { tone: "professional" }); if (data?.bio) { setBio(data.bio); toast.success("AI bio drafted — review & save"); } }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setGenBusy(false); }
  };
  const saveBio = async () => {
    setSavingBio(true);
    try { await api.patch("/profile/bio", { bio, bio_active: bioActive }); await checkAuth(); toast.success("Bio saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingBio(false); }
  };

  const loadContacts = () => api.get("/email-contacts").then(({ data }) => setContacts(data)).catch(() => {});
  useEffect(() => { loadContacts(); }, []);
  const renameContact = async (c, name) => {
    if (name === c.name) return;
    try { await api.patch(`/email-contacts/${c.contact_id}`, { name }); toast.success("Recipient renamed"); loadContacts(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const removeContact = async (c) => {
    try { await api.delete(`/email-contacts/${c.contact_id}`); toast.success("Recipient removed"); loadContacts(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const savePrefs = async () => {
    setSavingPrefs(true);
    try { await api.patch("/profile/preferences", { lead_preferences: prefs }); await checkAuth(); toast.success("Lead preferences saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingPrefs(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/upload/avatar", fd); await checkAuth(); toast.success("Profile picture updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload/logo", fd); setOrg((o) => ({ ...o, logo: data.path })); toast.success("Company logo updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  useEffect(() => {
    api.get("/org").then(({ data }) => { setOrg(data); setName(data?.name || ""); setOrgTypes(data?.insurance_types || []); setBrandColor(data?.brand_color || "#0D9488"); setPartnerApproval(!!data?.require_partner_approval); setAutoBook(!!data?.auto_book_on_won); setOrgAddress(data?.address || ""); }).catch(() => {});
  }, []);

  const togglePartnerApproval = async (v) => {
    setPartnerApproval(v);
    try { await api.post("/org/partner-approval", { require_partner_approval: v }); toast.success(v ? "Agent-initiated partnerships now need admin approval" : "Partner approval requirement turned off"); }
    catch (err) { setPartnerApproval(!v); toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleAutoBook = async (v) => {
    setAutoBook(v);
    try { const { data } = await api.patch("/org", { name, insurance_types: orgTypes, brand_color: brandColor, auto_book_on_won: v }); setOrg(data); toast.success(v ? "Won leads will auto-add to your Book of Business" : "Auto-add to Book turned off"); }
    catch (err) { setAutoBook(!v); toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.patch("/org", { name, insurance_types: orgTypes, brand_color: brandColor, address: orgAddress }); setOrg(data); toast.success("Agency updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold sm:text-3xl">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Manage your agency workspace and profile.</p>
      </div>

      <Tabs defaultValue="me" className="w-full">
      <TabsList data-testid="settings-tabs">
        <TabsTrigger value="me" data-testid="tab-my-settings">My settings</TabsTrigger>
        <TabsTrigger value="company" data-testid="tab-company">Company</TabsTrigger>
      </TabsList>

      <TabsContent value="me" className="mt-6 space-y-6" data-testid="settings-me">
      <PushToggleCard />
      <Card className="p-6">
        <h3 className="font-heading font-semibold">Your profile</h3>
        <div className="mt-4 flex items-center gap-4">
          <Avatar className="h-16 w-16">
            {user.picture && <AvatarImage src={fileUrl(user.picture)} alt="" />}
            <AvatarFallback className="text-lg">{initials(user.name)}</AvatarFallback>
          </Avatar>
          <label className="cursor-pointer">
            <span className="inline-flex items-center rounded-lg border border-border px-3 py-2 text-sm hover:bg-accent" data-testid="upload-avatar-label"><Upload className="mr-2 h-4 w-4" /> Upload photo</span>
            <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="avatar-file-input" />
          </label>
        </div>
        <div className="mt-4 space-y-3 text-sm">
          <div className="flex items-center justify-between border-b border-border pb-3">
            <span className="text-muted-foreground">Name</span><span className="font-medium">{user.name}</span>
          </div>
          <div className="flex items-center justify-between border-b border-border pb-3">
            <span className="flex items-center gap-2 text-muted-foreground"><Mail className="h-4 w-4" /> Email</span><span className="font-medium">{user.email}</span>
          </div>
          <div className="flex items-center justify-between border-b border-border pb-3">
            <span className="flex items-center gap-2 text-muted-foreground"><ShieldCheck className="h-4 w-4" /> Role</span>
            <span className="font-medium capitalize">{user.role}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Sign-in method</span>
            <span className="font-medium capitalize">{user.auth_provider === "google" ? "Google" : "Email & password"}</span>
          </div>
        </div>
      </Card>

      <Card className="p-6" data-testid="closer-bio-card">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15"><UserCircle className="h-5 w-5 text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">Closer status & bio</h3>
            <p className="text-xs text-muted-foreground">Closers get surfaced on leads matching their products. Publish a bio so teammates can learn about you.</p>
          </div>
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm"><Checkbox checked={isCloser} onCheckedChange={(v) => toggleCloser(!!v)} disabled={savingCloser} data-testid="closer-toggle" /> I'm a <span className="font-medium">Closer</span> — surface me on matching leads</label>
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <Label>Your bio</Label>
            <Button size="sm" variant="outline" onClick={genBio} disabled={genBusy} data-testid="generate-bio-button">{genBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Generate with AI</Button>
          </div>
          <Textarea className="mt-2" rows={4} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Write a short professional bio, or generate one with AI…" data-testid="bio-input" />
          <label className="mt-3 flex items-center gap-2 text-sm"><Checkbox checked={bioActive} onCheckedChange={(v) => setBioActive(!!v)} data-testid="bio-active-toggle" /> Publish my bio (teammates can read it on matching leads)</label>
          <Button onClick={saveBio} disabled={savingBio} className="mt-4" data-testid="save-bio-button">{savingBio && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save bio</Button>
        </div>
      </Card>

      <Card className="p-6" data-testid="lead-preferences-card">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15"><Filter className="h-5 w-5 text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">Lead preferences</h3>
            <p className="text-xs text-muted-foreground">Only receive auto-assigned leads for the products you tick. Leave all unticked to receive every type.</p>
          </div>
        </div>
        <div className="mt-4">
          <Label>Products I want to receive</Label>
          <InsuranceTypePicker value={prefs} onChange={setPrefs} />
          <p className="mt-3 text-xs text-muted-foreground">{prefs.length === 0 ? "Currently receiving all lead types." : `Receiving ${prefs.length} selected type${prefs.length > 1 ? "s" : ""} only.`}</p>
          <Button onClick={savePrefs} disabled={savingPrefs} className="mt-4" data-testid="save-preferences-button">
            {savingPrefs && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save preferences
          </Button>
        </div>
      </Card>

      <Card className="p-6" data-testid="profile-phone-card">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15"><PhoneCall className="h-5 w-5 text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">Your phone number</h3>
            <p className="text-xs text-muted-foreground">US format (###) ###-#### · visible to your teammates.</p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Input value={phone} onChange={(e) => setPhone(fmtPhone(e.target.value))} placeholder="(555) 123-4567" maxLength={14} className="max-w-xs" data-testid="profile-phone-input" />
          <Button onClick={savePhone} disabled={savingPhone} data-testid="save-phone-button">Save</Button>
        </div>
      </Card>

      <Card className="p-6" data-testid="softphone-card">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15"><PhoneCall className="h-5 w-5 text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">Softphone</h3>
            <p className="text-xs text-muted-foreground">Connect your dialer so the Call button on leads opens your softphone.</p>
          </div>
        </div>
        <div className="mt-4 space-y-4">
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={spEnabled} onCheckedChange={(v) => setSpEnabled(!!v)} data-testid="softphone-enabled" /> Route Click-to-Call through my softphone</label>
          <div className={spEnabled ? "" : "pointer-events-none opacity-50"}>
            <Label>Dialer type</Label>
            <Select value={spScheme} onValueChange={setSpScheme}>
              <SelectTrigger className="mt-1.5 max-w-sm" data-testid="softphone-scheme"><SelectValue /></SelectTrigger>
              <SelectContent>{SOFTPHONE_SCHEMES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
            {spScheme === "custom" && (
              <div className="mt-3">
                <Label>Click-to-dial URL template</Label>
                <Input className="mt-1.5" placeholder="e.g. https://dialer.example.com/call?to={number}" value={spTemplate} onChange={(e) => setSpTemplate(e.target.value)} data-testid="softphone-template" />
                <p className="mt-1 text-xs text-muted-foreground">Use <code>{"{number}"}</code> for the phone digits (works with RingCentral, Zoiper, 8x8, Twilio dialers).</p>
              </div>
            )}
            {spScheme === "ringcentral" && (
              <p className="mt-3 text-xs text-muted-foreground">RingCentral: install the RingCentral app and sign in on this device — the Call button will open RingCentral to dial the lead.</p>
            )}
          </div>
          <Button onClick={saveSoftphone} disabled={savingSp} data-testid="save-softphone-button">{savingSp && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save softphone</Button>
        </div>
      </Card>

      <Card className="p-6" data-testid="email-contacts-card">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15"><Mail className="h-5 w-5 text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">Saved email recipients</h3>
            <p className="text-xs text-muted-foreground">Rename or remove the contacts you pick from when emailing a lead's info.</p>
          </div>
        </div>
        <div className="mt-4 space-y-2" data-testid="email-contacts-list">
          {contacts.length === 0 && <p className="text-sm text-muted-foreground">No saved recipients yet. Save one from a lead's “Email this lead's info” panel.</p>}
          {contacts.map((c) => (
            <div key={c.contact_id} className="flex items-center gap-2" data-testid={`contact-row-${c.contact_id}`}>
              <Input defaultValue={c.name} placeholder="Name" className="max-w-[220px]" data-testid={`contact-name-${c.contact_id}`}
                onBlur={(e) => renameContact(c, e.target.value.trim())} />
              <span className="flex-1 truncate text-sm text-muted-foreground">{c.email}</span>
              <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => removeContact(c)} data-testid={`contact-delete-${c.contact_id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </Card>
      </TabsContent>

      <TabsContent value="company" className="mt-6 space-y-6" data-testid="settings-company">
      <Card className="p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15"><Building2 className="h-5 w-5 text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">Agency</h3>
            <p className="text-xs text-muted-foreground">Your isolated multi-tenant workspace.</p>
          </div>
        </div>
        <div className="mt-5">
          <Label>Agency name</Label>
          <div className="mt-1.5 flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!isAdmin} data-testid="org-name-input" />
            {isAdmin && <Button onClick={save} disabled={saving} data-testid="save-org-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save</Button>}
          </div>
          {!isAdmin && <p className="mt-2 text-xs text-muted-foreground">Only admins can rename the agency.</p>}
        </div>
        {isAdmin && (
          <div className="mt-5">
            <Label>Agency address</Label>
            <p className="mt-1 text-xs text-muted-foreground">Start typing to pick a Google-verified address. Used on branded exports.</p>
            <div className="mt-1.5 flex gap-2">
              <div className="flex-1"><AddressAutocomplete value={orgAddress} onChange={(e) => setOrgAddress(e.target.value)}
                onSelect={(a) => setOrgAddress([a.address, a.city, [a.state, a.zip].filter(Boolean).join(" ")].filter(Boolean).join(", "))}
                placeholder="123 Main St, City, ST 00000" data-testid="org-address-input" /></div>
              <Button onClick={save} disabled={saving} data-testid="save-org-address-button">Save</Button>
            </div>
          </div>
        )}
        {isAdmin && (
          <div className="mt-5">
            <Label>Company logo</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted">
                {org?.logo ? <img src={fileUrl(org.logo)} alt="logo" className="h-full w-full object-contain" /> : <Building2 className="h-6 w-6 text-muted-foreground" />}
              </div>
              <label className="cursor-pointer">
                <span className="inline-flex items-center rounded-lg border border-border px-3 py-2 text-sm hover:bg-accent" data-testid="upload-logo-label"><Upload className="mr-2 h-4 w-4" /> Upload logo</span>
                <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
              </label>
            </div>
          </div>
        )}
        {isAdmin && (
          <div className="mt-5" data-testid="org-products">
            <Label>Products we handle</Label>
            <p className="mt-1 text-xs text-muted-foreground">Shown in the agency directory so partners can find you by insurance type.</p>
            <InsuranceTypePicker value={orgTypes} onChange={setOrgTypes} />
          </div>
        )}
      </Card>

      <Card className="p-6" data-testid="brand-card">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15"><Palette className="h-5 w-5 text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">Brand color</h3>
            <p className="text-xs text-muted-foreground">Used with your logo on exported lead PDFs so they look client-ready.</p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <input type="color" value={brandColor} onChange={(e) => setBrandColor(e.target.value)} data-testid="brand-color-input" className="h-10 w-16 cursor-pointer rounded border border-border bg-transparent" />
          <span className="font-mono text-sm text-muted-foreground">{brandColor}</span>
          <Button onClick={save} disabled={saving} data-testid="save-brand-button">Save brand</Button>
        </div>
      </Card>

      <JoinCodeCard user={user} />

      {isAdmin && (
        <Card className="p-6" data-testid="partner-approval-card">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15"><ShieldCheck className="h-5 w-5 text-primary" /></div>
            <div>
              <h3 className="font-heading font-semibold">Partnership approval</h3>
              <p className="text-xs text-muted-foreground">Keep oversight of cross-agency connections your agents start.</p>
            </div>
          </div>
          <label className="mt-4 flex items-start gap-3 text-sm">
            <Checkbox checked={partnerApproval} onCheckedChange={(v) => togglePartnerApproval(!!v)} data-testid="partner-approval-toggle" className="mt-0.5" />
            <span>
              <span className="font-medium">Require admin approval for agent-initiated partnerships</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">When on, a partner request an agent starts stays "Pending admin approval" and isn't sent to the other agency until you (or a manager) approve it. Admin/manager requests still send instantly.</span>
            </span>
          </label>
        </Card>
      )}
      {isAdmin && (
        <Card className="p-6" data-testid="auto-book-card">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15"><BookOpen className="h-5 w-5 text-primary" /></div>
            <div>
              <h3 className="font-heading font-semibold">Auto-add won leads to Book</h3>
              <p className="text-xs text-muted-foreground">Turn a closed-won lead into a Book of Business policy automatically.</p>
            </div>
          </div>
          <label className="mt-4 flex items-start gap-3 text-sm">
            <Checkbox checked={autoBook} onCheckedChange={(v) => toggleAutoBook(!!v)} data-testid="auto-book-toggle" className="mt-0.5" />
            <span>
              <span className="font-medium">Automatically add a lead to the Book of Business when it's marked Won</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">The policy is created for the assigned agent using the lead's name, company (as carrier), phone, address and premium. You can still add won leads manually anytime.</span>
            </span>
          </label>
        </Card>
      )}
      </TabsContent>
      </Tabs>
    </div>
  );
}
